/*
 * fit-size-picker.js
 *
 * Makes the dependent Fit -> Size picker behave correctly.
 *
 * The picker is filtered server-side in product-variant-options.liquid: for a
 * given Fit only the Sizes that exist for that Fit are rendered, and a valid
 * Size is force-checked in the markup. The catch is that Dawn resolves the
 * actual variant (price / Add-to-cart / the data-selected-variant JSON) from
 * the `option_values` it posts, which it builds from every currently-checked
 * input at the moment a Fit radio changes — including the *old* Size, which may
 * be invalid for the new Fit (e.g. Kids + "M"). That produces an invalid
 * combination and Dawn ends up on a null / first-available variant that does
 * not match the Fit the shopper just clicked.
 *
 * Primary fix (capture phase): clearing the Size is NOT enough — Dawn would
 * then post only [Fit] and the server defaults the unspecified Size to the first
 * size BY POSITION (XXS), which doesn't exist for Kids (null variant) and is sold
 * out for Standard. Instead, when a value in the FIRST option group (Fit) changes
 * we retarget the checked Size radio's data-option-value-id to the first IN-STOCK
 * size for that Fit (from the server map #FitFirstSize-<sectionId>) *before* Dawn
 * reads the selection. Dawn then posts a complete, valid, in-stock combination.
 *
 * Safety net (variant-change): if, after a re-render, a radio group still ends
 * up with nothing checked, or a sold-out radio checked while an in-stock one
 * exists, move selection to the first in-stock radio and fire a change.
 */
(function () {
  function getOptionFieldsets(selects) {
    return Array.prototype.slice.call(
      selects.querySelectorAll('fieldset.product-form__input')
    );
  }

  function readFitSizeMap(selects) {
    var sectionId = selects.dataset.section;
    var el = sectionId ? document.getElementById('FitFirstSize-' + sectionId) : null;
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // When the first option (Fit) changes, retarget the dependent (Size) selection
  // to a valid in-stock value for the new Fit so Dawn posts a complete, valid
  // combination (the server otherwise defaults Size to the first by position).
  function handleLeadingOptionChange(selects, event) {
    var target = event.target;
    if (!target || target.tagName !== 'INPUT' || target.type !== 'radio') return;

    var fieldsets = getOptionFieldsets(selects);
    if (fieldsets.length < 2) return;
    if (!fieldsets[0].contains(target)) return;

    var map = readFitSizeMap(selects);
    var fitId = target.dataset.optionValueId;
    var targetSizeId = map && fitId ? map[fitId] : null;
    if (!targetSizeId) return;

    var sizeFieldset = fieldsets[1];
    var checked = sizeFieldset.querySelector('input[type="radio"]:checked');
    if (!checked) {
      checked = sizeFieldset.querySelector('input[type="radio"]');
      if (checked) checked.checked = true;
    }
    if (checked) {
      // Dawn's selectedOptionValues getter reads data-option-value-id; point it
      // at the valid size. This radio is about to be replaced by the re-render.
      checked.dataset.optionValueId = String(targetSizeId);
    }
  }

  function ensureValidSize(selects) {
    if (!selects) return;

    var fieldsets = getOptionFieldsets(selects);
    fieldsets.forEach(function (fs) {
      var radios = fs.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;

      // Only correct a group that has NOTHING checked. We intentionally do not
      // move a shopper off a sold-out size they deliberately picked — that size
      // stays selected with a truthful "Sold out" button (standard behavior).
      var anyChecked = Array.prototype.some.call(radios, function (r) {
        return r.checked;
      });
      if (anyChecked) return;

      var target =
        Array.prototype.filter.call(radios, function (r) {
          return !r.classList.contains('disabled');
        })[0] || radios[0];

      if (target) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function bindLeadingOptionReset(selects) {
    if (!selects || selects.dataset.fitResetBound === 'true') return;
    selects.dataset.fitResetBound = 'true';
    // Capture phase so this runs before Dawn's bubble-phase change handler,
    // which reads the selected option values to build the fetch request.
    selects.addEventListener(
      'change',
      function (event) {
        handleLeadingOptionChange(selects, event);
      },
      true
    );
  }

  function bindAll() {
    document.querySelectorAll('variant-selects').forEach(bindLeadingOptionReset);
  }

  function init() {
    bindAll();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var sectionId = event && event.data ? event.data.sectionId : null;
        var selects = sectionId
          ? document.querySelector('#variant-selects-' + sectionId)
          : document.querySelector('variant-selects');
        // The re-rendered <variant-selects> is a new element, so re-bind and
        // let it settle before checking for an empty group.
        requestAnimationFrame(function () {
          bindLeadingOptionReset(selects);
          ensureValidSize(selects);
        });
      });
    }

    document.querySelectorAll('variant-selects').forEach(ensureValidSize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
